<template>
  <section class="Page" :style="{ height: pageHeight }">
    <Header class="Page__header" :showServiceMenu="false" />
    <main ref="mainRef" class="Page__main">
      <GlobalNavigation
        class="Page__globalNavigation"
        :globalMenuItems="globalMenuItems"
      />
      <ScrollBox class="Page__scrollBox" always>
        <div ref="userDivRef" class="Page__userDiv">
          <section class="Page__userSection">
            <Breadcrumb class="Page__breadcrumb" :breadcrumb="breadcrumb" />
            <Heading
              type="secondary"
              level="2"
              :heading="messages.heading.userManagement"
              class="Page__heading"
            />
            <UserManagement :isInviteUser="isInviteUser" />
          </section>
        </div>
      </ScrollBox>
    </main>
  </section>
</template>

<script lang="ts">
export default {
  name: 'AdminUsers',
};
</script>

<script lang="ts" setup>
import Header from '@spectee/components/Organisms/Header/index.vue';
import Breadcrumb from '@spectee/components/Molecules/Breadcrumb/index.vue';
import GlobalNavigation from '@spectee/components/Organisms/GlobalNavigation/index.vue';
import { globalMenuItems } from '../../data/SideMenuItems';
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import UserManagement from '@spectee/components/Organisms/UserManagement/index.vue';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import { useWindowHeight } from '@spectee/composables/useWindowHeight';
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import messages from '../../messages.json';

const { pageHeight } = useWindowHeight();

const state = window.history.state;
const isInviteUser = state?.isInviteUser ? state.isInviteUser : false;

const breadcrumb = [
  { link: '/', text: messages.heading.home },
  { text: messages.heading.userManagement },
];

const mainRef = ref<HTMLElement | null>(null);
const userDivRef = ref<HTMLElement | null>(null);

const setContentsHeight = async () => {
  if (mainRef.value && userDivRef.value) {
    userDivRef.value.style.height = '';

    await nextTick();

    const mainStyle = getComputedStyle(mainRef.value);
    const mainHeight = parseFloat(mainStyle.height);
    const headerHeight = 24;
    const userContentHeight = mainHeight - headerHeight;

    const userTableVisibleHeight = 500;
    const height =
      userContentHeight > userTableVisibleHeight
        ? userContentHeight
        : userTableVisibleHeight;
    userDivRef.value.style.height = `${height}px`;
  }
};

onMounted(() => {
  setContentsHeight();
  window.addEventListener('resize', setContentsHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', setContentsHeight);
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.Page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
  min-height: 0;
  min-width: 0;

  & &__header {
    position: unset;
  }
  & &__main {
    position: relative;
    display: flex;
    height: 100%;
    overflow: hidden;
    padding: 0;
    flex-grow: 1;
  }

  &__scrollBox {
    flex: 1;
  }

  &__userDiv {
    flex: 1;
    overflow: auto;
    min-width: $service-min-width;
  }

  &__userSection {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__breadcrumb,
  &__heading {
    padding: $service-unit * 4 $service-unit * 8 0;
  }

  @media (min-width: 768px) {
    & &__globalNavigation {
      position: unset;
    }
  }

  @media (max-width: 767px) {
    &__scrollBox {
      flex: 1;
      margin-left: $service-unit * 5;
    }
    &__breadcrumb,
    &__heading {
      padding: $service-unit * 4 $service-unit * 5 0;
    }
  }
}
</style>
